import { useId } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import loomOnlyIllustration from './assets/panel-illustration-loom-only.png';
import teamworkCollectionIllustration from './assets/panel-illustration-teamwork-collection.png';
import { OptInPreviewVideo } from './opt-in-preview-video';
import { type ChoiceValue } from './types';

/**
 * Right-hand marketing panel for the TWCG-2108 Loom Land opt-in modal.
 *
 * Built in two layers so the design stays editable in one place:
 *  1. {@link PanelBackground} — the Blue200 field, white swirl and cross-hatch
 *     grid, drawn as an inline SVG shared by every variant/selection.
 *  2. A transparent, full-panel @2x PNG per selection containing only the
 *     foreground illustration (product cards, arrows, cursor pill). The cards
 *     contain photographic content, so a raster is far lighter than an SVG
 *     export (~70KB vs ~1.6MB with embedded images).
 *
 * Both layers share the Figma "Graphic (Right side)" 484x601 coordinate space,
 * so they always stay aligned as the panel scales. On load, a short Loom clip
 * plays over the active illustration's screen ({@link OptInPreviewVideo}).
 */

// Figma "Graphic (Right side)" frame size; the background viewBox and the
// foreground PNGs (exported at 2x: 968x1202) share this coordinate space.
const PANEL_WIDTH = 484;
const PANEL_HEIGHT = 601;

const ILLUSTRATIONS: Record<ChoiceValue, string> = {
	'teamwork-collection': teamworkCollectionIllustration,
	'loom-only': loomOnlyIllustration,
};

// Cross-hatch grid geometry from the Figma "grid" frame: evenly spaced lines,
// masked to a rotated rectangle and clipped to the grid frame.
const GRID_SPACING = 37.2851;
const GRID_FIRST_ROW_Y = 60.7576;
const GRID_FIRST_COLUMN_X = 28.375;
const GRID_ROW_COUNT = 14;
const GRID_COLUMN_COUNT = 13;
const GRID_ROWS = Array.from(
	{ length: GRID_ROW_COUNT },
	(_, i) => GRID_FIRST_ROW_Y + i * GRID_SPACING,
);
const GRID_COLUMNS = Array.from(
	{ length: GRID_COLUMN_COUNT },
	(_, i) => GRID_FIRST_COLUMN_X + i * GRID_SPACING,
);

const panelCss = css({
	display: 'none',
	position: 'relative',
	flex: '1 1 50%',
	width: '50%',
	minWidth: 0,
	alignSelf: 'stretch',
	overflow: 'hidden',
	// Fills any height beyond the 484x601 artwork so the field reads as continuous.
	backgroundColor: wacToken('color.extended.blue200'),
	// Hidden on narrow viewports so the content column stays usable.
	'@media (min-width: 768px)': {
		display: 'block',
	},
});

// Locks both layers to the artwork's aspect ratio, anchored to the top and
// spanning the full panel width (nothing is cropped horizontally).
const artboardCss = css({
	position: 'relative',
	width: '100%',
	aspectRatio: `${PANEL_WIDTH} / ${PANEL_HEIGHT}`,
});

const layerCss = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	width: '100%',
	height: '100%',
});

const illustrationCss = css({
	display: 'block',
	opacity: 0,
	transition: 'opacity 200ms ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const activeIllustrationCss = css({
	opacity: 1,
});

/**
 * Shared decorative background: Blue200 field, white swirl and Blue300 grid.
 * Mask/clip ids are per-instance so multiple panels never collide.
 */
export const PanelBackground = () => {
	const id = useId();
	const maskId = `${id}-grid-mask`;
	const clipId = `${id}-grid-clip`;

	return (
		<svg
			css={layerCss}
			viewBox={`0 0 ${PANEL_WIDTH} ${PANEL_HEIGHT}`}
			fill="none"
			aria-hidden="true"
			focusable="false"
			data-testid="loom-land-opt-in-panel-background"
		>
			<rect width={PANEL_WIDTH} height={PANEL_HEIGHT} fill={wacToken('color.extended.blue200')} />
			{/* The swirl matches the modal surface so it flows out of the content column. */}
			<path
				d="M185 0.0348145C16.5 183.961 0 620 0 620V0H135L185 0.0348145Z"
				fill={token('elevation.surface')}
			/>
			<defs>
				<clipPath id={clipId}>
					<rect width="879.37" height="512.862" transform="translate(-131 55)" />
				</clipPath>
				<mask id={maskId} maskUnits="userSpaceOnUse" x="8" y="41" width="476" height="528">
					<path d="M272.848 41.1989L483.84 422.516L219.485 568.791L8.49235 187.473Z" fill="white" />
				</mask>
			</defs>
			<g
				clipPath={`url(#${clipId})`}
				mask={`url(#${maskId})`}
				stroke={wacToken('color.extended.blue300')}
			>
				{GRID_ROWS.map((y) => (
					<line key={`row-${y}`} x1="-176" y1={y} x2="795" y2={y} />
				))}
				{GRID_COLUMNS.map((x) => (
					<line key={`column-${x}`} x1={x} y1="-219" x2={x} y2="942" />
				))}
			</g>
		</svg>
	);
};

export type OptInGraphicPanelProps = {
	/** The illustration to show for the current selection. */
	active: ChoiceValue;
	/**
	 * Every illustration this modal can switch to. All are mounted up front and
	 * cross-faded, so changing selection never flashes while an image loads.
	 * Defaults to just {@link active}.
	 */
	illustrations?: ChoiceValue[];
	/** Override the Loom clip played on load (see {@link OptInPreviewVideo}). */
	videoSrc?: string;
};

export const OptInGraphicPanel = ({
	active,
	illustrations = [active],
	videoSrc,
}: OptInGraphicPanelProps) => (
	<div css={panelCss} role="presentation" data-testid="loom-land-opt-in-graphic-panel">
		<div css={artboardCss}>
			<PanelBackground />
			{illustrations.map((illustration) => (
				<Image
					key={illustration}
					css={[layerCss, illustrationCss, illustration === active && activeIllustrationCss]}
					src={ILLUSTRATIONS[illustration]}
					alt=""
					aria-hidden="true"
					loading="eager"
					data-testid={`loom-land-opt-in-illustration-${illustration}`}
					data-active={illustration === active}
				/>
			))}
			<OptInPreviewVideo illustration={active} src={videoSrc} />
		</div>
	</div>
);
